import type { SpoolMeta, SpoolSession } from '@/speech/contracts';
import type { MeetingCaptureState } from './types';
import type { MeetingRecorderDeps } from './meeting-recorder-deps';
import type { ChunkUploader } from './meeting-recorder-uploader';
import type { Active, Settled } from './meeting-recorder';
import { CAPTURE_FRAME_SAMPLES, CAPTURE_FRAMES_PER_CHUNK } from './meeting-capture-chunks';

type RecoveryDeps = {
  generation: () => number;
  newRun: (deps: MeetingRecorderDeps, owner: string) => Active;
  uploaderFor: (
    run: Active,
    meetingId: string,
    take: number,
    session: SpoolSession,
  ) => ChunkUploader;
  queueRest: (uploader: ChunkUploader, lastSeq: number, held?: Set<number>) => void;
  endTake: (
    run: Active,
    reason: 'stop' | 'reload' | 'crash',
    held?: Set<number>,
  ) => Promise<Settled<unknown>>;
};

const isClosedCapture = (error: unknown) => {
  const status = (error as { status?: unknown } | null)?.status;
  const code = (error as { code?: unknown } | null)?.code;
  return status === 404 || (status === 409 && code === 'meeting_capture_closed');
};

export function createMeetingRecorderRecovery(options: RecoveryDeps) {
  const current = (expectedGeneration: number) => {
    if (options.generation() !== expectedGeneration) throw new Error('recorder_retired');
  };

  async function reconcileSession(
    deps: MeetingRecorderDeps,
    owner: string,
    listed: SpoolMeta,
    expectedGeneration: number,
  ) {
    const meetingId = listed.meetingId!;
    const take = listed.take!;
    let state: MeetingCaptureState;
    try {
      state = await deps.api.captureState(meetingId);
    } catch (error) {
      current(expectedGeneration);
      if (isClosedCapture(error)) {
        await deps.spool.remove(listed.id).catch(() => {});
        return null;
      }
      throw error;
    }
    current(expectedGeneration);
    if (!state || state.status !== 'capturing') {
      await deps.spool.remove(listed.id).catch(() => {});
      return null;
    }
    const session = await deps.spool.open({ ...listed, state: 'stopped' });
    current(expectedGeneration);
    const run = options.newRun(deps, owner);
    run.meetingId = meetingId;
    run.take = take;
    run.session = session;
    run.recovered = true;
    run.lastSeq = listed.lastSeq;
    run.totalSamples = listed.samples;
    let serverTake = state.takes.find((item) => item.take === take);
    if (!serverTake) {
      const sources = state.takes[0]?.sources ?? ['mic'];
      try {
        await deps.api.putTake(meetingId, take, {
          sources,
          sampleRate: 16000,
          startedAt: new Date(listed.createdAt).toISOString(),
          deviceId: deps.deviceId(),
        });
      } catch (error) {
        current(expectedGeneration);
        if (!isClosedCapture(error)) throw error;
        await deps.spool.remove(listed.id).catch(() => {});
        return null;
      }
      current(expectedGeneration);
      serverTake = {
        take,
        sources,
        lastSeq: null,
        ended: false,
        storedChunks: [],
        missingChunks: [],
      };
    }
    const held = run.ackedChunks;
    for (const [first, last] of serverTake.storedChunks ?? []) {
      for (let chunk = first; chunk <= last; chunk += 1) {
        held.add(chunk);
        // Coordinates establish retention, not duration. Only a matching ack
        // retained by this same take supplies its original decoded sample count.
        const receipt = listed.meetingChunks?.[chunk];
        if (
          receipt?.acknowledged === true &&
          Number.isSafeInteger(receipt.samples) &&
          receipt.samples > 0 &&
          receipt.samples <= CAPTURE_FRAME_SAMPLES * CAPTURE_FRAMES_PER_CHUNK &&
          /^[a-f0-9]{64}$/.test(receipt.sha256)
        ) {
          if (run.ackedSamples !== null) run.ackedSamples += receipt.samples;
        } else run.ackedSamples = null;
      }
      await session.deleteRange(first * 100, last * 100 + 99);
      current(expectedGeneration);
    }
    run.uploader = options.uploaderFor(run, meetingId, take, session);
    current(expectedGeneration);
    if (!serverTake.ended) await options.endTake(run, 'reload', held);
    else {
      options.queueRest(run.uploader, run.lastSeq, held);
    }
    return { run, state, meta: listed };
  }

  async function recoverSession(
    deps: MeetingRecorderDeps,
    owner: string,
    sessionId: string,
    expectedGeneration: number,
  ) {
    const listed = (await deps.spool.list(owner, 'meeting')).find((meta) => meta.id === sessionId);
    if (!listed?.meetingId || listed.take === undefined) return null;
    current(expectedGeneration);
    const release = await deps.takeLock.tryHold(listed.id);
    if (!release) return null;
    try {
      const recovered = await reconcileSession(deps, owner, listed, expectedGeneration);
      if (recovered) recovered.run.releaseLock = release;
      else release();
      return recovered;
    } catch (error) {
      release();
      throw error;
    }
  }

  return { recoverSession };
}
